import {useMutation, UseMutationResult, useQuery, useQueryClient, UseQueryResult} from '@tanstack/react-query';
import {regionsApi} from '../api';
import {EditRegionParams, RegionModel} from '../model';
import {useNotifications} from '../../../shared';

type RegionByIdState = {
  getRegion: UseQueryResult<RegionModel, Error>;
  updateRegionMutation: UseMutationResult<RegionModel, Error, EditRegionParams>;
  refetchRegion: () => void;
  deleteRegionMutation: UseMutationResult<void, Error, string>;
};

export const useRegionById = (regionId: string): RegionByIdState => {
  const queryClient = useQueryClient();
  const {createNotification} = useNotifications();

  const getRegion = useQuery<RegionModel, Error>({
    queryKey: ['region', regionId],
    queryFn: () => regionsApi.getRegionById(regionId),
    enabled: !!regionId,
  });

  const refetchRegion = () => {
    queryClient.invalidateQueries({queryKey: ['region', regionId]});
  };

  const updateRegionMutation = useMutation<RegionModel, Error, EditRegionParams>({
    mutationFn: ({regionId, name, order}) => regionsApi.updateRegion({regionId, name, order}),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['region', regionId]});
      queryClient.invalidateQueries({queryKey: ['regions', 'infinite']});
      createNotification({
        title: 'Регион успешно обновлен',
        name: 'updateRegion',
        theme: 'success',
      });
    },
  });

  const deleteRegionMutation = useMutation<void, Error, string>({
    mutationFn: (regionId) => regionsApi.deleteRegion(regionId),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['regions', 'infinite']});
      createNotification({
        title: 'Регион успешно удален',
        name: 'deleteRegion',
        theme: 'success',
      });
    },
  });

  return {
    getRegion,
    updateRegionMutation,
    refetchRegion,
    deleteRegionMutation,
  };
};
